import React, { useEffect, useState } from 'react';
import { X, Keyboard } from 'lucide-react';
import { getKeybinds, matchBind } from '../game/keybinds';

// v2.4 QoL — Global keybind cheat sheet. Toggle with `?` (rebindable) or Shift+/. Ignored while typing in inputs.

export default function HotkeyCheatSheet() {
  const [open, setOpen] = useState(false);
  const [binds, setBinds] = useState(() => getKeybinds());

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (['INPUT','TEXTAREA','SELECT'].includes(tag)) return;
      if (matchBind('cheatsheet', e)) {
        e.preventDefault();
        setBinds(getKeybinds()); // refresh in case rebound
        setOpen(v => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const kbLabel = (k) => (k === ' ' ? 'SPACE' : (k || '').toUpperCase());
  const KEYS = [
    { key: 'ESC', desc: 'Close modal / back' },
    { key: kbLabel(binds.pause), desc: 'Pause / unpause during combat' },
    { key: 'ENTER', desc: 'End turn in combat (with confirm)' },
    { key: kbLabel(binds.endTurn), desc: 'End turn' },
    { key: '1-9', desc: 'Play card at that slot' },
    { key: kbLabel(binds.undo), desc: 'Undo last card played this turn' },
    { key: kbLabel(binds.toggleLog), desc: 'Toggle battle log' },
    { key: kbLabel(binds.openDeck), desc: 'Open deck viewer' },
    { key: kbLabel(binds.openMap), desc: 'Open map / minimap' },
    { key: 'RIGHT-CLICK', desc: 'Zoom card' },
    { key: 'R', desc: 'Randomize avatar (in editor)' },
    { key: kbLabel(binds.cheatsheet), desc: 'Toggle this cheat sheet' },
  ];

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={() => setOpen(false)}
      data-testid="hotkey-cheat-sheet"
    >
      <div
        className="brutal-border bg-[#0a0a0a] max-w-lg w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: 'var(--neon)', boxShadow: '8px 8px 0 var(--neon)' }}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 text-[color:var(--text-dim)] hover:text-white"
          data-testid="hotkey-close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Keyboard size={18} className="text-[color:var(--neon)]"/>
          <h2 className="font-display font-black text-2xl uppercase text-[color:var(--neon)]">HOTKEYS</h2>
        </div>
        <div className="space-y-1.5 text-sm" data-testid="hotkey-list">
          {KEYS.map(k => (
            <div key={k.key} className="flex items-center justify-between gap-3 border-b border-[#222] pb-1">
              <kbd className="px-2 py-0.5 bg-[#111] border border-[color:var(--neon)] text-[color:var(--neon)] font-mono text-xs uppercase min-w-[60px] text-center">
                {k.key}
              </kbd>
              <span className="text-[color:var(--text-dim)] text-right flex-1">{k.desc}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">
          Press <kbd className="px-1 bg-[#111] border border-[color:var(--text-dim)]">?</kbd> anytime to toggle
        </div>
      </div>
    </div>
  );
}
